import { formatMoney } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { listBills, type Bill } from "@/lib/api";
import {
  billBalanceCents,
  isPartPaid,
  matchesBillListFilter,
  recordDueLabel,
} from "@/lib/worklist";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function statusTone(
  status: string,
): "success" | "warning" | "danger" | "neutral" {
  switch (status.toLowerCase()) {
    case "paid":
      return "success";
    case "overdue":
      return "danger";
    case "received":
      return "warning";
    default:
      return "neutral";
  }
}

export function ToPayPanel({
  refreshKey,
  onPay,
}: {
  refreshKey: number;
  onPay: (billId: string) => void;
}) {
  const [bills, setBills] = useState<Bill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "received" | "overdue">("");

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listBills()
      .then((rows) => {
        if (!cancelled) setBills(rows);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(toErrorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const payable = useMemo(
    () =>
      (bills ?? [])
        .filter((bill) => billBalanceCents(bill) > 0)
        .filter(
          (bill) =>
            bill.status === "received" || bill.status === "overdue",
        ),
    [bills],
  );

  const receivedCount = payable.filter((bill) =>
    matchesBillListFilter(bill, "received"),
  ).length;
  const overdueCount = payable.filter((bill) =>
    matchesBillListFilter(bill, "overdue"),
  ).length;

  const visible = payable
    .filter((bill) => !status || matchesBillListFilter(bill, status))
    .filter((bill) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return (
        bill.bill_number.toLowerCase().includes(query) ||
        bill.supplier_name.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => a.due_at.localeCompare(b.due_at));

  return (
    <div className="grid gap-3">
      <div className="surface grid gap-3 rounded-2xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label
            htmlFor="to-pay-search"
            className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
          >
            Search
            <Input
              id="to-pay-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Bill or supplier…"
            />
          </label>
          <label
            htmlFor="to-pay-status"
            className="grid min-w-40 gap-1 text-xs font-medium"
          >
            Status
            <select
              id="to-pay-status"
              className="select"
              value={status}
              onChange={(event) =>
                setStatus(
                  event.target.value === "received" ||
                    event.target.value === "overdue"
                    ? event.target.value
                    : "",
                )
              }
            >
              <option value="">All open bills ({payable.length})</option>
              <option value="received">
                Awaiting payment ({receivedCount})
              </option>
              <option value="overdue">Overdue ({overdueCount})</option>
            </select>
          </label>
        </div>
      </div>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}

      {bills === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : payable.length === 0 ? (
        <EmptyState
          title="Nothing to pay"
          reason="Received and overdue bills with a balance appear here. Create or recognize a bill first."
        />
      ) : visible.length === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          No open bills match these filters.
        </p>
      ) : (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="to-pay-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((bill) => {
                    const currency = bill.currency_code || "USD";
                    return (
                      <TableRow key={bill.id}>
                        <TableCell className="font-mono text-xs">
                          <Link
                            to="/bills/$billId"
                            params={{ billId: bill.id }}
                            className="hover:underline"
                          >
                            {bill.bill_number}
                          </Link>
                        </TableCell>
                        <TableCell>{bill.supplier_name}</TableCell>
                        <TableCell>
                          {new Date(bill.due_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-2">
                            <Badge tone={statusTone(bill.status)}>
                              {bill.status}
                            </Badge>
                            {isPartPaid(bill) ? (
                              <span className="text-xs text-[var(--muted-foreground)]">
                                part paid
                              </span>
                            ) : null}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney({
                            cents: billBalanceCents(bill),
                            currency,
                          })}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => onPay(bill.id)}
                            >
                              Record payment
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="to-pay-stack"
          >
            {visible.map((bill) => {
              const currency = bill.currency_code || "USD";
              const due = recordDueLabel(bill);
              return (
                <article
                  key={bill.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                    <Link
                      to="/bills/$billId"
                      params={{ billId: bill.id }}
                      className="record-title underline-offset-4 hover:underline"
                    >
                      {bill.bill_number}
                    </Link>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: billBalanceCents(bill),
                        currency,
                      })}
                    </span>
                  </div>
                  <CardField label="Supplier">{bill.supplier_name}</CardField>
                  <CardField label="Due">
                    {new Date(bill.due_at).toLocaleDateString()}
                  </CardField>
                  <CardField label="Status">
                    <span className="inline-flex items-center gap-2">
                      <Badge
                        tone={statusTone(bill.status)}
                        className="justify-self-start"
                      >
                        {bill.status}
                      </Badge>
                      {isPartPaid(bill) ? (
                        <span className="text-xs text-[var(--muted-foreground)]">
                          part paid
                        </span>
                      ) : null}
                    </span>
                  </CardField>
                  <CardField label="Timing">
                    <Badge tone={due.tone} className="justify-self-start">
                      {due.label}
                    </Badge>
                  </CardField>
                  <div className="flex justify-end pt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => onPay(bill.id)}
                    >
                      Record payment
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
